import {
  QuestionApiModel,
  QuestionProgress,
  QuestionTag,
  ShortAnswerQuestionGroup,
  ShortAnswerSubQuestion,
} from '../types/questions';

export const isWrittenResponseType = (questionType: string | null | undefined): boolean =>
  questionType === 'SA' || questionType === 'ER';

const normalizeSubQuestionKey = (value: string | null | undefined): string => {
  if (!value) return 'N/A';
  const trimmed = value.trim().toLowerCase();
  if (!trimmed || ['n/a', 'na', 'nan', 'none', 'main'].includes(trimmed)) {
    return 'N/A';
  }
  return trimmed.replace(/[().\s]/g, '');
};

const buildGroupKey = (question: QuestionApiModel): string =>
  `${question.question_type}-${question.year}-${question.exam_type}-${question.question_number}`;

const mergeProgress = (
  current: QuestionProgress | null | undefined,
  incoming: QuestionProgress | null | undefined
): QuestionProgress | null | undefined => {
  if (!incoming || !incoming.last_attempt_at) {
    return current;
  }

  if (!current || !current.last_attempt_at) {
    return incoming;
  }

  const currentTime = new Date(current.last_attempt_at).getTime();
  const incomingTime = new Date(incoming.last_attempt_at).getTime();

  if (Number.isNaN(incomingTime)) {
    return current;
  }
  if (Number.isNaN(currentTime) || incomingTime > currentTime) {
    return incoming;
  }

  return current;
};

const mergeTags = (
  existing: QuestionTag[] | undefined,
  incoming: QuestionTag[] | undefined
): QuestionTag[] | undefined => {
  if (!incoming || incoming.length === 0) {
    return existing;
  }
  const seen = new Map<string, QuestionTag>();
  (existing ?? []).forEach((tag) => seen.set(tag.id, tag));
  incoming.forEach((tag) => {
    if (!seen.has(tag.id)) {
      seen.set(tag.id, tag);
    }
  });
  return Array.from(seen.values());
};

const buildSubQuestion = (question: QuestionApiModel, normalizedSubQuestion: string): ShortAnswerSubQuestion => {
  const options = [
    { key: 'A', text: question.option_a },
    { key: 'B', text: question.option_b },
    { key: 'C', text: question.option_c },
    { key: 'D', text: question.option_d },
    ...(question.option_e ? [{ key: 'E', text: question.option_e }] : []),
  ].filter((option) => option.text && option.text.trim().length > 0);

  return {
    id: question.id,
    sub_question: normalizedSubQuestion,
    question_text: question.question_text,
    options,
    correct_answer: question.correct_answer,
    difficulty_rating: question.difficulty_rating,
    marks: question.marks ?? null,
    worked_solution: question.worked_solution,
    progress: question.progress,
    is_bookmarked: question.is_bookmarked,
  };
};

const compareSubQuestions = (a: ShortAnswerSubQuestion, b: ShortAnswerSubQuestion) => {
  const normalize = (value: string) => (value === 'N/A' ? '' : normalizeSubQuestionKey(value));
  const aValue = normalize(a.sub_question);
  const bValue = normalize(b.sub_question);
  if (aValue === bValue) return 0;
  return aValue < bValue ? -1 : 1;
};

export type PracticeQuestionSetItem = QuestionApiModel | ShortAnswerQuestionGroup;
export type PracticeQuestionTypeSelection = 'MC' | 'SA' | 'ER' | 'BOTH' | string;

export const groupShortAnswerQuestions = (
  questions: QuestionApiModel[]
): ShortAnswerQuestionGroup[] => {
  const groups = new Map<
    string,
    {
      group: ShortAnswerQuestionGroup;
      totalMarks: number;
      weightedDifficulty: number;
      hasUnknownDifficulty: boolean;
      subQuestionKeys: Set<string>;
    }
  >();

  const normalizeMarks = (marks: number | null | undefined): number => {
    if (typeof marks === 'number' && marks > 0) {
      return marks;
    }
    return 1;
  };

  questions.forEach((question) => {
    if (!isWrittenResponseType(question.question_type)) {
      return;
    }

    const key = buildGroupKey(question);
    const normalizedSubQuestion = normalizeSubQuestionKey(question.sub_question);
    const subQuestion = buildSubQuestion(question, normalizedSubQuestion);
    const marks = normalizeMarks(question.marks);
    const difficultyValue = typeof question.difficulty_rating === 'number' ? question.difficulty_rating : null;
    const hasDifficulty = difficultyValue !== null;
    const weightedDifficulty = hasDifficulty ? difficultyValue * marks : 0;

    if (!groups.has(key)) {
      const group: ShortAnswerQuestionGroup = {
        id: `${question.question_type}-${key}`,
        year: question.year,
        exam_type: question.exam_type,
        question_number: question.question_number,
        question_type: question.question_type as 'SA' | 'ER',
        vce_area_of_study: question.vce_area_of_study,
        difficulty_rating: question.difficulty_rating,
        total_marks: marks,
        tags: question.tags,
        progress: question.progress,
        sub_questions: [subQuestion],
      };
      groups.set(key, {
        group,
        totalMarks: marks,
        weightedDifficulty,
        hasUnknownDifficulty: !hasDifficulty,
        subQuestionKeys: new Set([normalizedSubQuestion]),
      });
      return;
    }

    const entry = groups.get(key)!;
    if (!entry.subQuestionKeys.has(normalizedSubQuestion)) {
      entry.subQuestionKeys.add(normalizedSubQuestion);
      entry.group.sub_questions.push(subQuestion);
      entry.totalMarks += marks;
      entry.weightedDifficulty += weightedDifficulty;
      entry.hasUnknownDifficulty = entry.hasUnknownDifficulty || !hasDifficulty;
    }
    entry.group.progress = mergeProgress(entry.group.progress, question.progress);
    entry.group.tags = mergeTags(entry.group.tags, question.tags);
  });

  return Array.from(groups.values()).map(({ group, totalMarks, weightedDifficulty, hasUnknownDifficulty }) => {
    const normalizedTotal = totalMarks > 0 ? totalMarks : 0;
    const averageDifficulty =
      !hasUnknownDifficulty && normalizedTotal > 0
        ? Math.round((weightedDifficulty / normalizedTotal) * 10) / 10
        : null;
    return {
      ...group,
      difficulty_rating: averageDifficulty,
      total_marks: normalizedTotal || group.total_marks,
      sub_questions: [...group.sub_questions].sort(compareSubQuestions),
    };
  });
};

export const buildPracticeQuestionSet = (
  apiQuestions: QuestionApiModel[],
  questionTypeSelection: PracticeQuestionTypeSelection = 'BOTH'
): PracticeQuestionSetItem[] => {
  const includeMultipleChoice = questionTypeSelection === 'BOTH' || questionTypeSelection === 'MC';
  const includeShortAnswer = questionTypeSelection === 'BOTH' || questionTypeSelection === 'SA';
  const includeExtendedResponse = questionTypeSelection === 'BOTH' || questionTypeSelection === 'ER';

  const mcQuestions = includeMultipleChoice
    ? apiQuestions.filter((item) => item.question_type === 'MC')
    : [];

  const writtenQuestions = apiQuestions.filter((item) => {
    if (!isWrittenResponseType(item.question_type)) {
      return false;
    }
    if (item.question_type === 'SA') {
      return includeShortAnswer;
    }
    if (item.question_type === 'ER') {
      return includeExtendedResponse;
    }
    return false;
  });

  return [...mcQuestions, ...groupShortAnswerQuestions(writtenQuestions)].sort((a, b) => {
    if (a.year !== b.year) return a.year - b.year;
    if (a.exam_type !== b.exam_type) return a.exam_type - b.exam_type;
    if (a.question_number !== b.question_number) return a.question_number - b.question_number;
    return a.question_type.localeCompare(b.question_type);
  });
};
